/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import DatePicker from "react-datepicker";
import SearchMultiSelect from "../../components/SearchMultiSelect";
import fetchHelper from "../../services/fetchHelper";
import { LayoutContext } from "../../components/layout";

const imageSpinner = require("../../assets/esign-ui-icons/saving-ico-white.svg");
const checkIco = require("../../assets/esign-ui-icons/ico-check.svg");

const HeaderStep = ({ children, hasArrow = true }) => {
  return (
    <div
      style={{
        display: "flex",
        flex: 1,
        backgroundColor: "#eee",
        alignItems: "center",
        justifyContent: "center",
        padding: 3,
        position: "relative",
      }}
    >
      {children}
      {hasArrow ? <span style={{ position: "absolute", right: -20, top: 2 }}>{">"}</span> : null}
    </div>
  );
};

const CustomDateInput = ({ value, onClick }) => (
  <div className="date-custom-input" onClick={onClick} style={{ height: 35, marginRight: 20 }}>
    <div className="date-custom-input-content">{value}</div>
    <i className="ti ti-calendar" style={{ color: "#aaa" }}></i>
  </div>
);

const PatientInfo = ({
  disabled,
  initialValue = null,
  onSubmit = null,
  isLoading = false,
  title = "Patient Info",
}) => {
  const [patientId, setPatientId] = useState("");

  useEffect(() => {
    if (initialValue) setPatientId(initialValue);
  }, [initialValue]);

  return (
    <div style={{ display: "flex", flex: 1, flexDirection: "column" }}>
      <small>{title}</small>
      <div style={{ marginTop: 2, marginRight: 20, position: "relative" }}>
        <input
          disabled={disabled}
          className="ib-fix"
          type="text"
          style={{ width: "100%", top: 0, marginRight: 8 }}
          value={patientId}
          onChange={(e) => setPatientId(e.target.value)}
        />
        {onSubmit ? (
          <button
            className="btn btn-sm btn-teal"
            style={{ padding: "0 9px", position: "absolute", right: 0, top: "5px" }}
            onClick={(e) => onSubmit(patientId)}
            disabled={disabled || isLoading}
          >
            {isLoading ? (
              <img
                id="loading-spinner-1"
                src={imageSpinner}
                style={{ width: 20, height: 20, margin: "0 -2.5px" }}
              />
            ) : (
              <div
                className="check-flat-ico icon-64"
                style={{ marginRight: 0, width: "17px", height: "25px" }}
              ></div>
            )}
          </button>
        ) : null}
      </div>
    </div>
  );
};

const ReportId = ({ onFetchReport }) => {
  const [isCheckingIID, setIsCheckingIID] = useState(false);
  const [interrogationId, setInterrogationId] = useState(null);
  const { addToast } = useToasts();

  const fetchI = async () => {
    if (!navigator.onLine) return (window.location = "/auth/login");
    try {
      setIsCheckingIID(true);
      const LS = JSON.parse(localStorage.getItem("genToken"));
      const token = LS["token"];
      const parms = { headers: { "Content-Type": "application/json", token } };
      let url = "/patientmanagement/rest/interrogations/" + interrogationId;
      const r = await fetch(url, parms);
      if (r.ok) {
        const j = await r.json();
        onFetchReport(j);
      } else {
        addToast("Unable to confirm Report is FINAL", {
          appearance: "error",
          autoDismiss: false,
        });
      }
      setIsCheckingIID(false);
    } catch (error) {
      setIsCheckingIID(false);
      addToast("Unable to confirm Report is FINAL", {
        appearance: "error",
        autoDismiss: false,
      });
      console.log(error);
      console.log("Connectivity problem.  Please try again later.");
    }
  };

  return (
    <div
      style={{
        marginTop: 20,
        display: "flex",
        flex: 1,
        flexDirection: "column",
      }}
    >
      <small>Report ID</small>
      <div style={{ marginTop: 2, display: "flex", flex: 1, position: "relative" }}>
        <input
          className="ib-fix"
          type="text"
          style={{
            width: "auto",
            display: "flex",
            flex: 1,
            top: 0,
            marginRight: 8,
          }}
          onChange={(e) => setInterrogationId(e.target.value)}
        />
        <button
          className="btn btn-sm btn-teal"
          style={{ padding: "0 9px", position: "absolute", right: "8px", top: "5px" }}
          onClick={fetchI}
          disabled={isCheckingIID}
        >
          {isCheckingIID ? (
            <img
              id="loading-spinner-2"
              src={imageSpinner}
              style={{ width: 20, height: 20, margin: "0 -2.5px" }}
            />
          ) : (
            <div
              className="check-flat-ico icon-64"
              style={{ marginRight: 0, width: "17px", height: "25px" }}
            ></div>
          )}
        </button>
      </div>
    </div>
  );
};

const NewPage = () => {
  const { user } = useContext(LayoutContext);
  const [isCheckingPatientId, setIsCheckingPatientId] = useState(false);
  const [isValidInterrogationId, setIsValidInterrogationId] = useState(false);
  const [interrogationId, setInterrogationId] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [locations, setLocations] = useState([]);
  const [location, setLocation] = useState(null);
  const [locationLoc, setLocationLoc] = useState(null);
  const [locationPrelim, setLocationPrelim] = useState(null);
  const [isAutoPrelim, setIsAutoPrelim] = useState("");
  const [locAssignLoading, setLocAssignLoading] = useState(false);
  const [isAutoPrelimLoading, setIsAutoPrelimLoading] = useState(false);
  const [hub, setHub] = useState(null);
  const [hubLoc, setHubLoc] = useState(null);
  const [subLocation, setSubLocation] = useState("");
  const [activeStep, setActiveStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [hubAssignLoading, setHubAssignLoading] = useState(false);

  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [isLoadingUnfinalize, setIsLoadingUnfinalize] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [deviceId, setDeviceId] = useState(null);

  const [email, setEmail] = useState(null);
  const [activationLink, setActivationLink] = useState(null);
  const deviceIdoptions = [
    { value: 3014, label: "option 1" },
    { value: 3015, label: "option 2" },
    { value: 3016, label: "option 3" },
    { value: 3017, label: "option 4" },
  ];
  const { addToast } = useToasts();
  const [userInfo, setUserInfo] = useState(null);
  const [patientFromName, setPatientFromName] = useState(null);
  const [patientFromDevice, setPatientFromDevice] = useState(null);
  const [patientToDevices, setPatientToDevices] = useState([]);

  const fetchActivationLink = async (email) => {
    if (!navigator.onLine) return (window.location = "/auth/login");
    try {
      setIsCheckingEmail(true);
      const LS = JSON.parse(localStorage.getItem("genToken"));
      const token = LS["token"];
      const parms = { headers: { "Content-Type": "application/json", token } };
      let url = "/patientmanagement/rest/users/activationLink?email=" + email;
      const r = await fetch(url, parms);
      if (r.ok) {
        const { activationLink } = await r.json();
        setActivationLink(activationLink);
      } else {
        const { errors } = await r.json();
        errors &&
          errors.forEach((err) => {
            addToast(err.msg, {
              appearance: "error",
              autoDismiss: false,
            });
          });
      }
      setIsCheckingEmail(false);
    } catch (error) {
      setIsCheckingEmail(false);
      addToast(error && error.message, {
        appearance: "error",
        autoDismiss: false,
      });
      console.log(error);
      console.log("Connectivity problem.  Please try again later.");
    }
  };

  const fetchPatientDevices = async (patientId) => {
    if (!navigator.onLine) return (window.location = "/auth/login");
    try {
      setIsCheckingPatientId(true);
      const LS = JSON.parse(localStorage.getItem("genToken"));
      const token = LS["token"];
      const parms = { headers: { "Content-Type": "application/json", token } };
      let url = "/patientmanagement/rest/devices/findByPatientId/" + patientId;
      const r = await fetch(url, parms);
      if (r.ok) {
        const devices = await r.json();
        if (devices.length > 0) return devices;
        else {
          addToast("Patient hasn't any devices.", {
            appearance: "error",
            autoDismiss: false,
          });
          return [];
        }
      } else {
        addToast("Unable to fetch patient devices", {
          appearance: "error",
          autoDismiss: false,
        });
      }
      setIsCheckingPatientId(false);
    } catch (error) {
      setIsCheckingPatientId(false);
      addToast("Unable to fetch patient devices", {
        appearance: "error",
        autoDismiss: false,
      });
      console.log(error);
      console.log("Connectivity problem.  Please try again later.");
    }
  };

  useEffect(() => {
    fetchLocation();
    fetchUsers();
  }, []);

  useEffect(() => {
    const sl = locations.find((l) => l.value == location);
    setSubLocation(sl?.subLocations);
  }, [location]);

  useEffect(() => {
    if (user && user.locations) {
      setLocation(user?.locations[0].id);
      setLocationLoc(user?.locations[0].id);
      setLocationPrelim(user?.locations[0].id);
      setHubLoc(user?.locations[0].hub);
      if (user?.locations[0].isAutoPreReport) setIsAutoPrelim(user?.locations[0].isAutoPreReport);
    }
  }, [user]);

  const fetchLocation = async () => {
    try {
      let url = "/patientmanagement/rest/location/serviceReport";
      //   if (process.env.NODE_ENV === "development") url = "/serviceReport.json";
      const res = await fetchHelper.get({ url });
      setLocations(
        res
          .map((i) => ({ value: i.id, label: i.name, subLocations: i.subLocations }))
          .sort((a, b) => {
            if (a.label > b.label) return 1;
            else if (a.label < b.label) return -1;
            return 0;
          })
      );
    } catch (err) {
      setLocations([{ value: user?.locations[0]?.id, label: user?.locations[0]?.name }]);
    }
  };

  const fetchUsers = async () => {
    try {
      const urlParams = {
        where: JSON.stringify([{ rcc: { $eq: true } }]),
        match: "AND",
        matchInner: "OR",
        sort: "-createdAt",
      };
      const urlSearch = new URLSearchParams(urlParams);

      const users = await fetchHelper.get({
        url: `/patientmanagement/rest/users?${urlSearch}`,
        headers: {
          "X-Range": `items=0-49`,
          Range: `items=0-49`,
        },
      });
      setUsers(users.filter((i) => i.username));
    } catch (error) {
      console.log(error);
    }
  };

  const onMove = async () => {
    try {
      const lS = window.localStorage.getItem("genToken");
      let url = "/patientmanagement/rest/interrogations/move";
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          token: JSON.parse(lS)["token"],
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          device: { id: deviceId },
          id: interrogationId,
        }),
      });
      console.log(response);
    } catch (err) {
      console.log(err);
    }
  };

  const irStatus = async () => {
    setIsLoadingUnfinalize(true);
    try {
      const lS = window.localStorage.getItem("genToken");
      console.log(lS);
      let url = "/patientmanagement/rest/interrogations/irStatus";
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          token: JSON.parse(lS)["token"],
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          interrogationStatus: "PRELIMINARY",
          id: interrogationId,
        }),
      });
      setIsSuccess(true);
      console.log(response);
    } catch (err) {
      console.log(err);
    }
    setIsLoadingUnfinalize(false);
  };

  const onUpdateSubLocation = async () => {
    try {
      setIsLoading(true);
      await fetchHelper.put(`/patientmanagement/rest/location/${location}`, {
        id: location,
        subLocations: subLocation,
      });
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log(error);
    }
  };

  const onLocAssignment = async () => {
    try {
      setLocAssignLoading(true);
      await fetchHelper.put(`/patientmanagement/rest/location/${locationLoc}`, {
        id: locationLoc,
        hub: hubLoc,
      });
      setLocAssignLoading(false);
    } catch (error) {
      setLocAssignLoading(false);
      console.log(error);
    }
  };

  const onHubAssignment = async () => {
    try {
      setHubAssignLoading(true);
      await fetchHelper.put(`/patientmanagement/rest/users/${selectedUser}/updateUserHub`, {
        hub,
        id: selectedUser,
      });
      setHubAssignLoading(false);
    } catch (error) {
      console.log(error);
    }
  };

  const onSetIsAutoPrelim = async () => {
    try {
      setIsAutoPrelimLoading(true);
      await fetchHelper.put(`/patientmanagement/rest/location/${locationPrelim}`, {
        isAutoPreReport: isAutoPrelim,
        id: locationPrelim,
      });
      setIsAutoPrelimLoading(false);
    } catch (error) {
      console.log(error);
    }
  };

  const hubListGenerator = () => {
    return [
      "A",
      "B",
      "C",
      "D",
      "E",
      "F",
      "G",
      "H",
      "I",
      "J",
      "K",
      "L",
      "M",
      "N",
      "O",
      "P",
      "Q",
      "R",
      "S",
      "T",
      "U",
      "V",
      "W",
      "X",
      "Y",
      "Z",
    ].map((i) => `Hub${i}`);
  };

  let rp = {};
  if (userInfo) rp = userInfo.readingPhysician;
  return (
    <div>
      <span style={{ fontSize: 20 }}>RCC OPERATIONAL TASKS</span>
      <div
        style={{
          flex: 1,
          backgroundColor: "#fff",
          border: ".75px solid #ddd",
          borderRadius: 3,
          marginTop: 20,
          flexDirection: "column",
        }}
      >
        <div className="new-page-row-item">
          <div
            style={{
              display: "flex",
              width: "15%",
              flexDirection: "column",
              justifyContent: "space-between",
              paddingRight: 30,
              textAlign: "center",
              padding: 20,
              height: 90,
              alignItems: "center",
            }}
          >
            <div
              style={{
                display: "flex",
                flex: 1,
                borderRight: "solid 1px #eee",
                height: "100%",
                justifyContent: "center",
                alignItems: "center",
                padding: "0 20px",
              }}
            >
              <b>Move Report</b>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              width: "20%",
              flexDirection: "column",
              justifyContent: "space-between",
              paddingRight: 30,
            }}
          >
            <HeaderStep>
              <b>1.</b>
            </HeaderStep>
            <ReportId
              onFetchReport={(report) => {
                setActiveStep(3);
                setUserInfo(report);
                setInterrogationId(report.id);
                setPatientFromName(
                  report && report.patient && `${report.patient.name} ${report.patient.family}`
                );
                setPatientFromDevice(report.device);
              }}
            />
          </div>

          <div
            style={{
              display: "flex",
              width: "34.5%",
              flexDirection: "column",
              justifyContent: "space-between",
              paddingRight: 30,
            }}
          >
            <HeaderStep>
              <span>
                <b>2.</b> Patient Info and Device ID <b>FROM</b>
              </span>
            </HeaderStep>
            <div style={{ marginTop: 20, display: "flex", flex: 1 }}>
              <PatientInfo initialValue={patientFromName} disabled={true} />
              <div style={{ display: "flex", flex: 1, flexDirection: "column" }}>
                <small>Device SN</small>
                <div style={{ marginTop: 2 }}>
                  <input
                    disabled={true}
                    className="ib-fix"
                    type="text"
                    style={{ width: "100%", top: 0, marginRight: 8 }}
                    value={patientFromDevice && patientFromDevice.serialNumber}
                  />
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              width: "34.5%",
              flexDirection: "column",
              justifyContent: "space-between",
              paddingRight: 30,
            }}
          >
            <HeaderStep>
              <span>
                <b>3.</b> Patient Info and Device ID <b>TO</b>
              </span>
            </HeaderStep>
            <div style={{ marginTop: 20, display: "flex", flex: 1 }}>
              <PatientInfo
                title="Patient ID"
                onSubmit={async (patientId) => {
                  setActiveStep(4);
                  const devices = await fetchPatientDevices(patientId);
                  setPatientToDevices(devices);
                }}
                disabled={activeStep < 3}
              />
              <div style={{ display: "flex", flex: 1, flexDirection: "column" }}>
                <small>Device SN</small>
                <div style={{ marginTop: 2 }}>
                  {/* <SearchMultiSelect
                    containerStyle={{ marginBottom: 0 }}
                    small
                    options={patientToDevices}
                    maxSelect={1}
                    hasPreview={false}
                    onChange={(data) => setDeviceId(data[0])}
                  /> */}
                  <select
                    disabled={activeStep < 4}
                    onChange={(e) => setDeviceId(e.target.value)}
                    style={{ width: "100%" }}
                  >
                    <option value={""}>Select Device SN</option>
                    {patientToDevices.map((device) => {
                      return (
                        <option key={device.id} value={device.id}>
                          {device.serialNumber}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              width: "6%",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <HeaderStep hasArrow={false}>
              <b>4.</b>
            </HeaderStep>
            <div
              style={{
                marginTop: 20,
                display: "flex",
                flex: 1,
                flexDirection: "column",
              }}
            >
              <div style={{ width: 20, height: 14.71 }} />
              <button
                className="btn btn-success"
                style={{ height: 36, marginTop: 2 }}
                disabled={!deviceId}
                onClick={onMove}
              >
                <span>MOVE</span>
              </button>
            </div>
          </div>
        </div>

        <div className="new-page-row-item">
          <div
            style={{
              display: "flex",
              width: "15%",
              flexDirection: "column",
              justifyContent: "space-between",
              paddingRight: 30,
              textAlign: "center",
              padding: 20,
              height: 90,
              alignItems: "center",
            }}
          >
            <div
              style={{
                display: "flex",
                flex: 1,
                borderRight: "solid 1px #eee",
                height: "100%",
                justifyContent: "center",
                alignItems: "center",
                padding: "0 20px",
              }}
            >
              <b>Unfinalize Report</b>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              width: "20%",
              flexDirection: "column",
              justifyContent: "space-between",
              paddingRight: 30,
            }}
          >
            <ReportId
              onFetchReport={(report) => {
                setInterrogationId(report.id);
                setUserInfo(report);
              }}
            />
          </div>

          <div style={{ position: "relative", display: "flex", width: "75%" }}>
            {(userInfo || {}).interrogationStatus &&
            (userInfo || {}).interrogationStatus === "FINAL" &&
            !isSuccess ? (
              <>
                <div
                  style={{
                    display: "flex",
                    width: "20%",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    paddingRight: 0,
                  }}
                >
                  <div
                    style={{
                      marginTop: 0,
                      display: "flex",
                      flex: 1,
                      flexDirection: "column",
                    }}
                  >
                    <small>Signed Date</small>
                    <div
                      style={{
                        marginTop: 2,
                        display: "flex",
                        flex: 1,
                        paddingRight: 20,
                      }}
                    >
                      <input
                        className="ib-fix"
                        type="text"
                        style={{ width: "100%", top: 0, marginRight: 8 }}
                        value={new Date().toLocaleDateString()}
                        readOnly
                      />
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    width: "20%",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    paddingRight: 30,
                  }}
                >
                  <div
                    style={{
                      marginTop: 0,
                      display: "flex",
                      flex: 1,
                      flexDirection: "column",
                    }}
                  >
                    <small>Signed By</small>
                    <div style={{ marginTop: 2, display: "flex", flex: 1 }}>
                      <input
                        className="ib-fix"
                        type="text"
                        style={{
                          width: "auto",
                          display: "flex",
                          flex: 1,
                          top: 0,
                          marginRight: 8,
                        }}
                        value={(rp.name || "") + " " + (rp.family || "")}
                        readOnly
                      />
                      <button
                        className="btn btn-sm btn-success"
                        style={{ padding: "0 9px" }}
                        onClick={irStatus}
                      >
                        {isLoadingUnfinalize ? (
                          <img id="loading-spinner-3" src={imageSpinner} style={{ width: 20, height: 20 }} />
                        ) : (
                          "UNFINALIZE"
                        )}
                      </button>
                    </div>
                  </div>
                </div>
                {!rp.name ? (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "#fff",
                      opacity: 0.5,
                    }}
                  />
                ) : null}
              </>
            ) : null}
            {((userInfo || {}).interrogationStatus && (userInfo || {}).interrogationStatus !== "FINAL") ||
            isSuccess ? (
              <img id="loading-spinner-4" src={checkIco} style={{ width: 20, height: 20 }} />
            ) : null}
          </div>
        </div>
        <div className="new-page-row-item">
          {/* Email Verification */}
          <div
            style={{
              display: "flex",
              width: "15%",
              flexDirection: "column",
              justifyContent: "space-between",
              paddingRight: 30,
              textAlign: "center",
              padding: 20,
              height: 90,
              alignItems: "center",
            }}
          >
            <div
              style={{
                display: "flex",
                flex: 1,
                borderRight: "solid 1px #eee",
                height: "100%",
                justifyContent: "center",
                alignItems: "center",
                padding: "0 20px",
              }}
            >
              <b>Activation Link</b>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              width: "20%",
              flexDirection: "column",
              justifyContent: "space-between",
              paddingRight: 30,
            }}
          >
            <div
              style={{
                marginTop: 0,
                display: "flex",
                flex: 1,
                flexDirection: "column",
              }}
            >
              <small>Email</small>
              <div style={{ marginTop: 2, display: "flex", flex: 1 }}>
                <input
                  className="ib-fix"
                  type="text"
                  style={{
                    width: "auto",
                    display: "flex",
                    flex: 1,
                    top: 0,
                    marginRight: 8,
                  }}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <button
                  className="btn btn-sm btn-teal"
                  style={{ padding: "0 9px" }}
                  onClick={() => fetchActivationLink(email)}
                  disabled={isCheckingEmail}
                >
                  {isCheckingEmail ? (
                    <img
                      id="loading-spinner-5"
                      src={imageSpinner}
                      style={{ width: 20, height: 20, margin: "0 -2.5px" }}
                    />
                  ) : (
                    <i
                      className="check-flat-ico icon-64"
                      style={{ marginRight: 0, width: 15, height: 25 }}
                    ></i>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div style={{ position: "relative", display: "flex", width: "75%" }}>
            {activationLink ? <code>${activationLink}</code> : null}
          </div>
          {/* Email Verification */}
        </div>

        {localStorage.getItem("rccmgmt") === "true" ? (
          <div className="new-page-row-item">
            {/* Sub-location */}
            <div
              style={{
                display: "flex",
                width: "15%",
                flexDirection: "column",
                justifyContent: "space-between",
                paddingRight: 30,
                textAlign: "center",
                padding: 20,
                height: 90,
                alignItems: "center",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flex: 1,
                  borderRight: "solid 1px #eee",
                  height: "100%",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "0 20px",
                }}
              >
                <b>Sub-location</b>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                width: "20%",
                flexDirection: "column",
                justifyContent: "space-between",
                paddingRight: 30,
              }}
            >
              <div
                style={{
                  marginTop: 0,
                  display: "flex",
                  flex: 1,
                  flexDirection: "column",
                }}
              >
                <small>Locations</small>
                <div style={{ marginTop: 2, display: "flex", flex: 1 }}>
                  <select
                    name="locations"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    style={{ minWidth: 190 }}
                  >
                    <option value="">Select Location...</option>
                    {locations.map((l) => (
                      <option key={l.label} value={l.value}>
                        {l.label}
                      </option>
                    ))}
                  </select>
                  {location ? (
                    <input
                      style={{ margin: "0 10px", minWidth: 250, fontFamily: "courier new" }}
                      type="text"
                      placeholder="Type sub-location..."
                      value={subLocation}
                      onChange={(e) => {
                        const { value } = e.target;
                        setSubLocation(value);
                      }}
                    />
                  ) : null}
                  {subLocation ? (
                    <button
                      className="btn btn-teal"
                      onClick={onUpdateSubLocation}
                      disabled={isLoading || location != user?.locations[0].id}
                    >
                      {isLoading ? (
                        <div
                          id="results-loading-icon"
                          className="saving-white-ico icon-64"
                          style={{ width: 15, height: 15, marginRight: 3 }}
                        />
                      ) : null}{" "}
                      Save
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
            {/* Sub-location */}
          </div>
        ) : null}

        <div className="new-page-row-item">
          {/* Hub Assignment */}
          <div
            style={{
              display: "flex",
              width: "15%",
              flexDirection: "column",
              justifyContent: "space-between",
              paddingRight: 30,
              textAlign: "center",
              padding: 20,
              height: 90,
              alignItems: "center",
            }}
          >
            <div
              style={{
                display: "flex",
                flex: 1,
                borderRight: "solid 1px #eee",
                height: "100%",
                justifyContent: "center",
                alignItems: "center",
                padding: "0 20px",
              }}
            >
              <b>Hub Assignment</b>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              width: "20%",
              flexDirection: "column",
              justifyContent: "space-between",
              paddingRight: 30,
            }}
          >
            <div
              style={{
                marginTop: 0,
                display: "flex",
                flex: 1,
                flexDirection: "column",
              }}
            >
              <small>User</small>
              <div style={{ marginTop: 2, display: "flex", flex: 1 }}>
                <select
                  name="users"
                  value={selectedUser}
                  onChange={(e) => {
                    const { value } = e.target;
                    setSelectedUser(value);
                    const user = users.find((u) => u.id == value);
                    if (user && user.hub) setHub(user.hub);
                  }}
                  style={{ minWidth: 190 }}
                >
                  <option value="">Select User...</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.username}
                    </option>
                  ))}
                </select>
                {selectedUser ? (
                  <select
                    name="hubs"
                    value={hub}
                    onChange={(e) => setHub(e.target.value)}
                    style={{ minWidth: 190, margin: "0 10px" }}
                  >
                    <option value="">Select Hub...</option>
                    {hubListGenerator().map((l) => (
                      <option key={l} value={l}>
                        {l}
                      </option>
                    ))}
                  </select>
                ) : null}
                {hub ? (
                  <button className="btn btn-teal" onClick={onHubAssignment} disabled={hubAssignLoading}>
                    {hubAssignLoading ? (
                      <div
                        id="results-loading-icon-2"
                        className="saving-white-ico icon-64"
                        style={{ width: 15, height: 15, marginRight: 3 }}
                      />
                    ) : null}{" "}
                    Assign
                  </button>
                ) : null}
              </div>
            </div>
          </div>
          {/* Hub Assignment */}
        </div>
        <div className="new-page-row-item">
          {/* Loc Assignment */}
          <div
            style={{
              display: "flex",
              width: "15%",
              flexDirection: "column",
              justifyContent: "space-between",
              paddingRight: 30,
              textAlign: "center",
              padding: 20,
              height: 90,
              alignItems: "center",
            }}
          >
            <div
              style={{
                display: "flex",
                flex: 1,
                borderRight: "solid 1px #eee",
                height: "100%",
                justifyContent: "center",
                alignItems: "center",
                padding: "0 20px",
              }}
            >
              <b>Loc Assignment</b>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              width: "20%",
              flexDirection: "column",
              justifyContent: "space-between",
              paddingRight: 30,
            }}
          >
            <div
              style={{
                marginTop: 0,
                display: "flex",
                flex: 1,
                flexDirection: "column",
              }}
            >
              <small>Locations</small>
              <div style={{ marginTop: 2, display: "flex", flex: 1 }}>
                <select
                  name="locations"
                  value={locationLoc}
                  onChange={(e) => {
                    const { value } = e.target;
                    setLocationLoc(value);
                  }}
                  style={{ minWidth: 190 }}
                >
                  <option value="">Select Location...</option>
                  {locations.map((l) => (
                    <option key={l.label} value={l.value}>
                      {l.label}
                    </option>
                  ))}
                </select>
                {locationLoc ? (
                  <select
                    name="hubs"
                    value={hubLoc}
                    onChange={(e) => setHubLoc(e.target.value)}
                    style={{ minWidth: 190, margin: "0 10px" }}
                  >
                    <option value="">Select Hub...</option>
                    {hubListGenerator().map((l) => (
                      <option key={l} value={l}>
                        {l}
                      </option>
                    ))}
                  </select>
                ) : null}
                {hubLoc ? (
                  <button
                    className="btn btn-teal"
                    onClick={onLocAssignment}
                    disabled={locAssignLoading || locationLoc != user?.locations[0].id}
                  >
                    {locAssignLoading ? (
                      <div
                        id="results-loading-icon-3"
                        className="saving-white-ico icon-64"
                        style={{ width: 15, height: 15, marginRight: 3 }}
                      />
                    ) : null}{" "}
                    Assign
                  </button>
                ) : null}
              </div>
            </div>
          </div>
          {/* Loc Assignment */}
        </div>
        <div className="new-page-row-item">
          {/* Auto Prelim */}
          <div
            style={{
              display: "flex",
              width: "15%",
              flexDirection: "column",
              justifyContent: "space-between",
              paddingRight: 30,
              textAlign: "center",
              padding: 20,
              height: 90,
              alignItems: "center",
            }}
          >
            <div
              style={{
                display: "flex",
                flex: 1,
                borderRight: "solid 1px #eee",
                height: "100%",
                justifyContent: "center",
                alignItems: "center",
                padding: "0 20px",
              }}
            >
              <b>Auto Prelim</b>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              width: "20%",
              flexDirection: "column",
              justifyContent: "space-between",
              paddingRight: 30,
            }}
          >
            <div
              style={{
                marginTop: 0,
                display: "flex",
                flex: 1,
                flexDirection: "column",
              }}
            >
              <small>Locations</small>
              <div style={{ marginTop: 2, display: "flex", flex: 1 }}>
                <select
                  name="locations"
                  value={locationPrelim}
                  onChange={(e) => {
                    const { value } = e.target;
                    setLocationPrelim(value);
                  }}
                  style={{ minWidth: 190 }}
                >
                  <option value="">Select Location...</option>
                  {locations.map((l) => (
                    <option key={l.label} value={l.value}>
                      {l.label}
                    </option>
                  ))}
                </select>
                {locationPrelim ? (
                  <select
                    name="hubs"
                    value={isAutoPrelim}
                    onChange={(e) => setIsAutoPrelim(e.target.value)}
                    style={{ minWidth: 190, margin: "0 10px" }}
                  >
                    <option value="">Select AutoPrelim...</option>
                    {[
                      { text: "YES", value: true },
                      { text: "NO", value: false },
                    ].map((i) => (
                      <option key={i.text} value={i.value}>
                        {i.text}
                      </option>
                    ))}
                  </select>
                ) : null}
                {isAutoPrelim ? (
                  <button
                    className="btn btn-teal"
                    onClick={onSetIsAutoPrelim}
                    disabled={isAutoPrelimLoading || locationPrelim != user?.locations[0].id}
                  >
                    {isAutoPrelimLoading ? (
                      <div
                        id="results-loading-icon-4"
                        className="saving-white-ico icon-64"
                        style={{ width: 15, height: 15, marginRight: 3 }}
                      />
                    ) : null}{" "}
                    Save
                  </button>
                ) : null}
              </div>
            </div>
          </div>
          {/* Auto Prelim */}
        </div>
        <div className="new-page-row-item"></div>
        <div className="new-page-row-item"></div>
      </div>
    </div>
  );
};

export default NewPage;
